import React, { useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { ActivityFeed } from '../../components/portal/ActivityFeed';
import { NeedsAttention } from '../../components/portal/NeedsAttention';
import { CopyClientLink } from '../../components/portal/CopyClientLink';
import { projectStatus } from '../../utils/status';
import { BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from '../../components/portal/adminStyles';
interface ProjectsProps {
  /** The New Project form is opened from the header. */
  creating: boolean;
  setCreating: (open: boolean) => void;
}
export function Projects({
  creating,
  setCreating
}: ProjectsProps) {
  const {
    activeProjects,
    archivedProjects,
    templates,
    openProject,
    createProject,
    findExistingProject,
    reopenProject,
    notify
  } = usePortal();
  const [client, setClient] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [showArchive, setShowArchive] = useState(false);
  /** An existing project with the same client and name, awaiting a decision. */
  const [duplicate, setDuplicate] = useState<{
    id: string;
    client: string;
    name: string;
  } | null>(null);
  function create() {
    createProject({
      client: client.trim(),
      name: name.trim(),
      type: type.trim(),
      templateId
    });
    notify(`${client.trim()} project created`);
    setClient('');
    setName('');
    setType('');
    setTemplateId('');
    setDuplicate(null);
    setCreating(false);
  }
  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!client.trim() || !name.trim()) return;

    /* A warning, not a block — Portal may legitimately repeat a name. */
    const existing = findExistingProject(client.trim(), name.trim());
    if (existing && !duplicate) {
      setDuplicate({
        id: existing.id,
        client: existing.client,
        name: existing.name
      });
      return;
    }
    create();
  }

  /* The archive: the same list, kept off the working dashboard. */
  if (showArchive) {
    return <div data-id="element-731" data-mp="3tazskhs618qy">
        <button type="button" onClick={() => setShowArchive(false)} className="label group flex items-center gap-3 text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-732" data-mp="3tarb0cl3lf9f">
          <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform duration-150 ease-out group-hover:-translate-x-1" strokeWidth={1.25} data-id="element-733" data-mp="35en3bdz1dl0o" />
          Client Projects
        </button>

        <h1 className="mt-10 border-b border-ruleDark pb-8 font-display text-4xl leading-[0.95] tracking-tight sm:text-5xl" data-id="element-734" data-mp="3fc9tiwlscyzy">
          Archived Projects
        </h1>

        <ul className="m-0 list-none p-0" data-id="element-735" data-mp="3k9jhe7tj12lo">
          {archivedProjects.map(row => <li key={row.id} className="grid grid-cols-1 items-baseline gap-x-8 gap-y-4 border-b border-ruleDark py-8 sm:grid-cols-12" data-id="element-736" data-mp="uoogmvzf824w">
              <span className="font-display text-2xl leading-none sm:col-span-5" data-id="element-737" data-mp="2k3698rcitlta">{row.client}</span>
              <span className="font-display text-xl text-paper/70 sm:col-span-4" data-id="element-738" data-mp="2f54p033czgg8">{row.name}</span>
              <span className="flex flex-wrap items-center gap-4 sm:col-span-3 sm:justify-end" data-id="element-739" data-mp="2acbkngbsya4o">
                <button type="button" onClick={() => openProject(row.id)} className={BUTTON_QUIET} data-id="element-740" data-mp="25ea3x21ummwd">
                  Open Project
                </button>
                <button type="button" onClick={() => {
              reopenProject(row.id);
              notify(`${row.client} restored to Active Projects`);
            }} className={BUTTON_QUIET} data-id="element-741" data-mp="1wg7srtoeidvd">
                  Restore Project
                </button>
              </span>
            </li>)}
          {archivedProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-742" data-mp="2egalxbymwwbv">
              Nothing archived.
            </li>}
        </ul>

        <p className="label-sm mt-8 max-w-xl leading-relaxed text-paper/30" data-id="element-743" data-mp="24iczmu0c59lw">
          Nothing is deleted — deliverables, comments, approvals, version history, project
          information and the client link are all retained.
        </p>
      </div>;
  }
  return <div data-id="element-744" data-mp="1ur9y5r3husm3">
      {creating && <form onSubmit={submit} className="mb-12 border border-paper/25 px-6 py-8 sm:px-8" data-id="element-745" data-mp="1zj9tf68mcxcc">
          <h2 className="font-display text-2xl leading-none" data-id="element-746" data-mp="1lrva0yasuu4r">New Project</h2>
          <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2" data-id="element-747" data-mp="1ltpsm5ylma5o">
            <label className="block" data-id="element-748" data-mp="1qrrdmwfl4jjs">
              <span className="label block text-paper/40" data-id="element-749" data-mp="1qolnrez4tdn0">Client</span>
              <input value={client} onChange={event => setClient(event.target.value)} placeholder="e.g. Example Group" autoFocus className={FIELD} data-id="element-750" data-mp="1bu8qfyik2eeg" />
            </label>
            <label className="block" data-id="element-751" data-mp="t5wzeb778d19">
              <span className="label block text-paper/40" data-id="element-752" data-mp="t6zp4fssc9fl">Project Name</span>
              <input value={name} onChange={event => setName(event.target.value)} placeholder="e.g. Leadership Series" className={FIELD} data-id="element-753" data-mp="1767l6ztnj1fk" />
            </label>
            <label className="block" data-id="element-754" data-mp="1g3rpyiqq3fux">
              <span className="label block text-paper/40" data-id="element-755" data-mp="1c4sq3cjum9ac">Project Type</span>
              <input value={type} onChange={event => setType(event.target.value)} placeholder="e.g. Video Series" className={FIELD} data-id="element-756" data-mp="1pvo0it9zwyg1" />
            </label>
            <label className="block" data-id="element-757" data-mp="1zt22xoz33mpc">
              <span className="label block text-paper/40" data-id="element-758" data-mp="1zu3l0deip9gk">Start From (optional)</span>
              <select value={templateId} onChange={event => setTemplateId(event.target.value)} className={SELECT} data-id="element-759" data-mp="1uwbt3jdvhncd">
                <option value="" className={OPTION} data-id="element-760" data-mp="26y3a634myf72">
                  Blank project
                </option>
                {templates.map(template => <option key={template.id} value={template.id} className={OPTION} data-id="element-761" data-mp="2gmuym1ahmhd8">
                    {template.name}
                  </option>)}
              </select>
            </label>
          </div>
          {duplicate ? <div className="mt-10 max-w-xl border border-paper/30 px-6 py-6" data-id="element-762" data-mp="2pm87arqsf1ww">
              <p className="font-display text-xl leading-snug" data-id="element-763" data-mp="2umcsuw6jymtz">
                A project with this client and project name already exists.
              </p>
              <div className="mt-7 flex flex-wrap items-center gap-6" data-id="element-764" data-mp="2zjcxd2nr7hiz">
                <button type="button" onClick={() => {
            const id = duplicate.id;
            setDuplicate(null);
            setCreating(false);
            openProject(id);
          }} className={BUTTON_SOLID} data-id="element-765" data-mp="10a67j4jy2pny">
                  Open Existing Project
                </button>
                <button type="button" onClick={create} className={BUTTON_QUIET} data-id="element-766" data-mp="nyvkpm2jau7a">
                  Create Anyway
                </button>
                <button type="button" onClick={() => setDuplicate(null)} className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-767" data-mp="1gn16mc5bqp8p">
                  Cancel
                </button>
              </div>
            </div> : <div className="mt-10 flex flex-wrap items-center gap-6" data-id="element-768" data-mp="11oqpe7uciuf7">
              <button type="submit" className={BUTTON_SOLID} data-id="element-769" data-mp="3reudu7caty81">
                Create Project
              </button>
              <button type="button" onClick={() => setCreating(false)} className={BUTTON_QUIET} data-id="element-770" data-mp="3mfq4ngpdk0hp">
                Cancel
              </button>
            </div>}
          <p className="label-sm mt-8 max-w-lg leading-relaxed text-paper/30" data-id="element-771" data-mp="kxu41r8oxx2z">
            Templates are starting structures only — every deliverable can be added, renamed,
            reordered or removed afterwards.
          </p>
        </form>}

      <NeedsAttention data-id="element-772" data-mp="b0fpjx6bvidb" />

      <div className="mt-16 grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-16" data-id="element-773" data-mp="b1qs5to1ek2u">
        <section aria-labelledby="active-projects" className="lg:col-span-8" data-id="element-774" data-mp="yps65i3zispn">
          <h2 id="active-projects" className="label border-b border-ruleDark pb-4 text-paper/40" data-id="element-775" data-mp="13na7onfoa5nm">
            Active Projects
          </h2>
          <ul className="m-0 list-none p-0" data-id="element-776" data-mp="ty0dn219qcwv">
            {activeProjects.map(row => {
            const waiting = row.deliverables.filter(item => item.status === 'Ready for Review' && item.visibleToClient).length;
            const changes = row.deliverables.reduce((total, item) => total + item.changeRequests.filter(request => !request.resolved).length, 0);
            return <li key={row.id} className="border-b border-ruleDark" data-id="element-777" data-mp="3gurvzxv4njjj">
                  <div className="grid grid-cols-1 items-baseline gap-x-8 gap-y-3 sm:grid-cols-12" data-id="element-778" data-mp="3gww12j4riole">
                    {/* The project name stays the dominant item in the row. */}
                    <button type="button" onClick={() => openProject(row.id)} className="group grid grid-cols-1 items-baseline gap-x-8 gap-y-3 py-8 text-left sm:col-span-10 sm:grid-cols-10" data-id="element-779" data-mp="2o6mc1dbwdykr">
                      <span className="font-display text-3xl leading-none sm:col-span-6 sm:text-4xl" data-id="element-780" data-mp="2t4nwo2oz25yc">
                        {row.client}
                      </span>
                      <span className="font-display text-xl text-paper/70 sm:col-span-4" data-id="element-781" data-mp="8frhk6ot89ea">
                        {row.name}
                      </span>
                      <span className="label flex items-center gap-4 text-paper sm:col-span-10" data-id="element-782" data-mp="1szmy4utitfnh">
                        {changes > 0 ? 'Changes' : waiting > 0 ? 'With client' : projectStatus(row)}
                        <ArrowRightIcon className="h-3.5 w-3.5 shrink-0 opacity-40 transition-transform duration-150 ease-out group-hover:translate-x-1 group-hover:opacity-100" strokeWidth={1.25} data-id="element-783" data-mp="1j8s4v6huxfsj" />
                      </span>
                    </button>
                    <span className="pb-8 sm:col-span-2 sm:justify-self-end sm:pt-8" data-id="element-784" data-mp="10bimzts6kade">
                      <CopyClientLink slug={row.slug} projectId={row.id} data-id="element-785" data-mp="10dlk0xrr33rm" />
                    </span>
                  </div>
                </li>;
          })}
            {activeProjects.length === 0 && <li className="border-b border-ruleDark py-8 font-display text-xl text-paper/40" data-id="element-786" data-mp="2pqym0gce4fpk">
                Nothing in production.
              </li>}
          </ul>

          {archivedProjects.length > 0 && <button type="button" onClick={() => setShowArchive(true)} className="label-sm mt-10 text-paper/35 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper/70" data-id="element-787" data-mp="3289eguibodq3">
              Archived Projects · {archivedProjects.length}
            </button>}
        </section>

        <div className="lg:col-span-4 lg:border-l lg:border-ruleDark lg:pl-12" data-id="element-788" data-mp="2ja6q8hxlmktc">
          <ActivityFeed data-id="element-789" data-mp="2jc9plskn5qaq" />
        </div>
      </div>
    </div>;
}